/**
 * MapPanel — local geographic surface: clients & prospects by location, with a
 * density heatmap and a "while you're there" hub (pick a town → see who's near).
 *
 * Offline-safe by design (field use on the ThinkPad): no external map tiles.
 * Real client/lead locations from sage-business-mcp are projected onto a small
 * built-in Cape Cod / SE-Mass coordinate table and rendered as an SVG heat
 * scatter, plus a density-ranked town list that drills into the records there.
 * Towns we don't have coordinates for fall into an "Other locations" bucket.
 */
import { useMemo, useState } from "react";
import { MapPin, Navigation } from "lucide-react";
import McpCard from "../components/McpCard";
import { useMcpTool } from "../lib/useMcpTool";
import { PanelScroll } from "./ClientsPanel";
import { setChatContext } from "../lib/chatContext";

// Approx lat/lng for the towns R&G actually works in (Cape Cod + SE Mass).
const TOWN_COORDS: Record<string, [number, number]> = {
  provincetown: [42.058, -70.179], truro: [41.997, -70.05], wellfleet: [41.937, -70.033],
  eastham: [41.83, -69.974], orleans: [41.79, -69.989], brewster: [41.76, -70.082],
  chatham: [41.682, -69.96], harwich: [41.686, -70.076], dennis: [41.735, -70.193],
  yarmouth: [41.706, -70.229], "south yarmouth": [41.66, -70.18], barnstable: [41.7, -70.3],
  hyannis: [41.652, -70.283], centerville: [41.65, -70.35], osterville: [41.628, -70.383],
  mashpee: [41.648, -70.481], sandwich: [41.759, -70.494], falmouth: [41.551, -70.615],
  woods_hole: [41.526, -70.671], bourne: [41.741, -70.599], "buzzards bay": [41.745, -70.618],
  plymouth: [41.958, -70.667], wareham: [41.761, -70.719], edgartown: [41.389, -70.513],
  "vineyard haven": [41.454, -70.604], nantucket: [41.284, -70.099], boston: [42.36, -71.058],
};

// Bounding box for the projection (SE Mass).
const LAT_MIN = 41.25, LAT_MAX = 42.4, LNG_MIN = -71.1, LNG_MAX = -69.9;

const norm = (s: string) => s.toLowerCase().replace(/\s+/g, " ").trim();
const project = ([lat, lng]: [number, number]): [number, number] => [
  ((lng - LNG_MIN) / (LNG_MAX - LNG_MIN)) * 100,
  (1 - (lat - LAT_MIN) / (LAT_MAX - LAT_MIN)) * 100,
];

interface Bucket { town: string; clients: number; leads: number; records: Array<{ label: string; kind: "client" | "lead" }>; coord?: [number, number] }

interface Establishment { town?: string; state?: string; current_dba?: string; current_business_name?: string; lifecycle_stage?: string; primary_client_name?: string }
interface TopLead { name?: string; dba_name?: string; city?: string; state?: string; lead_temperature?: string }

export default function MapPanel() {
  // Establishments carry town + lifecycle (clients live here); lead pipeline top_leads carry city (prospects).
  const est = useMcpTool<{ establishments?: Establishment[] }>("business", "get_establishments_dashboard", { limit: 200 });
  const pipeline = useMcpTool<{ top_leads?: TopLead[] }>("business", "get_lead_pipeline", { limit: 50 });
  const [selected, setSelected] = useState<string | null>(null);

  const establishments = est.data?.establishments ?? [];
  const topLeads = pipeline.data?.top_leads ?? [];

  const buckets = useMemo(() => {
    const map = new Map<string, Bucket>();
    const add = (townRaw: string | undefined, kind: "client" | "lead", label: string) => {
      const t = (townRaw ?? "").toString().trim();
      if (!t) return;
      const key = norm(t);
      let b = map.get(key);
      if (!b) { b = { town: t, clients: 0, leads: 0, records: [], coord: TOWN_COORDS[key] }; map.set(key, b); }
      b[kind === "client" ? "clients" : "leads"]++;
      b.records.push({ label, kind });
    };
    establishments.forEach((e) => {
      const isClient = (e.lifecycle_stage ?? "").toUpperCase().includes("CLIENT");
      add(e.town, isClient ? "client" : "lead", e.current_dba ?? e.current_business_name ?? e.primary_client_name ?? "(venue)");
    });
    topLeads.forEach((l) => add(l.city, "lead", l.dba_name ?? l.name ?? "(lead)"));
    return [...map.values()].sort((a, b) => (b.clients + b.leads) - (a.clients + a.leads));
  }, [establishments, topLeads]);

  const maxTotal = Math.max(1, ...buckets.map((b) => b.clients + b.leads));
  const mapped = buckets.filter((b) => b.coord);
  const sel = buckets.find((b) => norm(b.town) === selected);
  const loading = est.loading || pipeline.loading;

  return (
    <PanelScroll accentVar="--biome-gardener" title="Map" subtitle="clients & prospects by location · while-you're-there">
      <McpCard title="Geographic heatmap" loading={loading} error={est.error ?? pipeline.error} onRefresh={() => { est.refresh(); pipeline.refresh(); }} accentVar="--biome-gardener">
        {/* SVG heat scatter */}
        <div className="relative w-full rounded-lg border border-border bg-bg overflow-hidden" style={{ aspectRatio: "16 / 9" }}>
          <svg viewBox="0 0 100 56" className="w-full h-full">
            <rect x="0" y="0" width="100" height="56" fill="var(--sidebar)" />
            {mapped.map((b) => {
              const [x, y] = project(b.coord!);
              const total = b.clients + b.leads;
              const r = 1.2 + (total / maxTotal) * 5;
              const isSel = norm(b.town) === selected;
              return (
                <g key={b.town} onClick={() => setSelected(isSel ? null : norm(b.town))} style={{ cursor: "pointer" }}>
                  <circle cx={x} cy={y * 0.56} r={r + 2.5} fill="var(--primary)" opacity={0.15} />
                  <circle cx={x} cy={y * 0.56} r={r} fill={b.clients > 0 ? "var(--biome-business)" : "var(--text-muted)"} opacity={isSel ? 1 : 0.85} stroke={isSel ? "var(--text)" : "none"} strokeWidth={0.4} />
                  <text x={x} y={y * 0.56 - r - 1} fontSize="2.2" fill="var(--text-muted)" textAnchor="middle">{b.town}</text>
                </g>
              );
            })}
          </svg>
          <div className="absolute bottom-1 right-2 text-[9px] text-text-dim flex items-center gap-2">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ background: "var(--biome-business)" }} /> clients</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ background: "var(--text-muted)" }} /> prospects only</span>
          </div>
        </div>

        {/* density-ranked towns */}
        <div className="mt-4 space-y-1.5">
          {buckets.length === 0 && <div className="text-body-sm text-text-dim">No location data on client/lead records.</div>}
          {buckets.slice(0, 18).map((b) => {
            const total = b.clients + b.leads;
            const isSel = norm(b.town) === selected;
            return (
              <button key={b.town} onClick={() => setSelected(isSel ? null : norm(b.town))} className="w-full flex items-center gap-3 text-left group">
                <span className="w-28 shrink-0 text-body-sm text-text truncate flex items-center gap-1">
                  <MapPin size={12} className={b.coord ? "text-primary" : "text-text-dim"} /> {b.town}
                </span>
                <span className="flex-1 h-3 rounded-full bg-surface-hover overflow-hidden">
                  <span className="block h-full rounded-full" style={{ width: `${(total / maxTotal) * 100}%`, background: b.clients > 0 ? "var(--biome-business)" : "var(--text-dim)" }} />
                </span>
                <span className="font-mono text-caption text-text-muted w-20 text-right shrink-0">{b.clients}c · {b.leads}p</span>
              </button>
            );
          })}
        </div>
      </McpCard>

      {/* while-you're-there hub */}
      {sel && (
        <McpCard title="" loading={false} error={null} accentVar="--biome-gardener">
          <div className="flex items-center gap-2 mb-2">
            <Navigation size={15} className="text-primary" />
            <span className="text-h3 text-text font-semibold">While you're in {sel.town}</span>
            <span className="text-caption text-text-dim">{sel.clients} clients · {sel.leads} prospects</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {sel.records.map((r, i) => (
              <button key={i} onClick={() => setChatContext({ label: `${r.label} (${sel.town})`, detail: `${r.label} — ${r.kind} in ${sel.town}, MA.` })}
                className="flex items-center gap-2 text-body-sm px-2 py-1 rounded-md bg-surface-hover hover:bg-surface-active text-left group">
                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: r.kind === "client" ? "var(--biome-business)" : "var(--text-dim)" }} />
                <span className="text-text truncate group-hover:text-primary transition-colors">{r.label}</span>
                <span className="ml-auto text-caption text-text-dim">{r.kind}</span>
              </button>
            ))}
          </div>
        </McpCard>
      )}
    </PanelScroll>
  );
}
